import { useState } from 'react';

import { P1_CATEGORY_META } from '../../telas/painel-p1/tela8-adapter';

import { ModalBase } from './ModalBase';

import type { P1Category } from '../../types';
import type React from 'react';

export interface ManualFlagModalProps {
  open: boolean;
  isPending: boolean;
  isError: boolean;
  onClose: () => void;
  onSubmit: (body: { conversationId: string; category: P1Category; reason: string }) => void;
}

const CATEGORIES = Object.keys(P1_CATEGORY_META) as P1Category[];

/** Type-guard p/ narrowar o value (string) do <select> sem type-assertion. */
function isP1Category(v: string): v is P1Category {
  return (CATEGORIES as string[]).includes(v);
}

/**
 * Flag manual de P1 (T8-3, `p1-manual-flag` Pro+ opt-in). Cria um P1Incident
 * forense a partir de uma conversa. Campos do `ManualFlagP1Dto` real (R15):
 * conversationId, category (enum 6), reason (min 3). Sem dado falso.
 */
export function ManualFlagModal({
  open,
  isPending,
  isError,
  onClose,
  onSubmit,
}: ManualFlagModalProps): React.JSX.Element | null {
  const [conversationId, setConversationId] = useState('');
  const [category, setCategory] = useState<P1Category | ''>('');
  const [reason, setReason] = useState('');

  const valid = conversationId.trim().length > 0 && category !== '' && reason.trim().length >= 3;

  return (
    <ModalBase
      open={open}
      onClose={onClose}
      title="Sinalizar incidente manual"
      width={560}
      footer={
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          <button className="btn-ghost-mini" type="button" onClick={onClose} disabled={isPending}>
            Cancelar
          </button>
          <button
            className="btn-ghost-mini btn-ghost-mini--primary"
            type="button"
            disabled={isPending || !valid}
            onClick={() => {
              if (category === '') return;
              onSubmit({
                conversationId: conversationId.trim(),
                category,
                reason: reason.trim(),
              });
            }}
          >
            {isPending ? 'Sinalizando…' : 'Sinalizar incidente'}
          </button>
        </div>
      }
    >
      <div className="modal-form">
        <div className="modal-form__callout modal-form__callout--danger">
          ⚠ Cria um incidente crítico forense vinculado a uma conversa. Use apenas para casos
          críticos reais (CSAM, ameaça, denúncia).
        </div>
        {isError && (
          <div className="modal-form__callout modal-form__callout--danger" role="alert">
            Não foi possível sinalizar o incidente. Verifique a conversa e tente novamente.
          </div>
        )}
        <label className="modal-form__field">
          <span className="modal-form__label">ID da conversa</span>
          <input
            className="modal-form__textarea"
            value={conversationId}
            onChange={(e) => {
              setConversationId(e.target.value);
            }}
          />
        </label>
        <label className="modal-form__field">
          <span className="modal-form__label">Categoria</span>
          <select
            className="modal-form__select"
            value={category}
            onChange={(e) => {
              const v = e.target.value;
              if (v === '' || isP1Category(v)) setCategory(v);
            }}
          >
            <option value="">Selecione…</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {P1_CATEGORY_META[c].label}
              </option>
            ))}
          </select>
        </label>
        <label className="modal-form__field">
          <span className="modal-form__label">Motivo (mín. 3 caracteres)</span>
          <textarea
            className="modal-form__textarea"
            rows={3}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
            }}
          />
        </label>
      </div>
    </ModalBase>
  );
}
